import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchEntryBySlug, fetchCollection } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import ScrollReveal from '../components/ScrollReveal';

function parseReviewDate(dateStr) {
  if (!dateStr) return 0;
  const parts = String(dateStr).trim().split('-');
  if (parts.length === 3) {
    if (parts[2].length === 4) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day).getTime();
    }
    if (parts[0].length === 4) {
      return new Date(dateStr).getTime();
    }
  }
  const parsed = new Date(dateStr).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    async function loadData() {
      try {
        const [homeItems, allReviews] = await Promise.all([
          fetchCollection('home_page'),
          fetchCollection('reviews')
        ]);
        const page = (Array.isArray(homeItems) && homeItems[0]) || await fetchEntryBySlug('da', 'index');
        setPageData(page);
        const sortedReviews = [...(allReviews || [])].sort((a, b) => parseReviewDate(b.date) - parseReviewDate(a.date));
        setReviews(sortedReviews); // Hent alle anmeldelser til karrusellen sorteret med nyeste først
      } catch (err) {
        console.error('Error loading home data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - visibleCount);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  // Standard metadata hvis CMS fejler
  const metaTitle = pageData?.meta_title?.da || pageData?.meta_title || "SKT Byg | Tømrer & Murer i Galten, Silkeborg & Skanderborg";
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description || "Søger du en pålidelig tømrer eller murer? SKT Byg leverer solidt håndværk, nybyggeri, renovering og tagarbejde i Galten og omegn. Få et uforpligtende tilbud!";

  // Baggrundsbillede til Hero
  const heroImage = (pageData?.hero_image && pageData.hero_image[0]) || "https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/imgi-4-faa-et-tilbud2.jpg";

  const heroTitle = pageData?.hero_title?.da || pageData?.hero_title || "Kvalitet og godt håndværk";
  const heroText = pageData?.hero_text?.da || pageData?.hero_text || "Med mere end 30 års erfaring som din lokale tømrer i Galten, Storring, Stjær og omegn leverer vi skræddersyede løsninger i højeste kvalitet, der holder i mange år fremover.";
  const heroUsp1 = pageData?.hero_usp_1?.da || pageData?.hero_usp_1 || "Overholdte aftaler";
  const heroUsp2 = pageData?.hero_usp_2?.da || pageData?.hero_usp_2 || "Junckers Gulvlægger";
  const heroBtnText = pageData?.hero_button_text?.da || pageData?.hero_button_text || "Få et uforpligtende tilbud";
  const heroSecBtnText = pageData?.hero_secondary_button_text?.da || pageData?.hero_secondary_button_text || "Se vores arbejde";
  const quickContactTitle = pageData?.quick_contact_title?.da || pageData?.quick_contact_title || "Har du et byggeprojekt?";
  const quickContactSubtitle = pageData?.quick_contact_subtitle?.da || pageData?.quick_contact_subtitle || "Kontakt Steffen direkte for en snak";
  const servicesTitle = pageData?.services_title?.da || pageData?.services_title || "Vi sikrer dig kvalitet og godt håndværk!";
  const servicesSubtitle = pageData?.services_subtitle?.da || pageData?.services_subtitle || "Godt håndværk er altid vores drivkraft. Vi gør meget ud af sammen med kunden at finde frem til den bedste løsning.";
  const qualityTitle = pageData?.quality_title?.da || pageData?.quality_title || "Vi vægter kvalitet fremfor alt!";
  const qualityText = pageData?.quality_text?.da || pageData?.quality_text || "Vi laver skræddersyede løsninger efter dine behov. Vores erfarne team af tømrere og murere står klar til at hjælpe dig med dit næste projekt.";
  const reviewsTitle = pageData?.reviews_title?.da || pageData?.reviews_title || "Glade og tilfredse kunder";
  const reviewsBtnText = pageData?.reviews_button_text?.da || pageData?.reviews_button_text || "Læs alle anmeldelser";
  const contactTitle = pageData?.contact_title?.da || pageData?.contact_title || "Skal vi løse din opgave?";
  const contactText = pageData?.contact_text?.da || pageData?.contact_text || "Skriv til os og få et uforpligtende tilbud på din opgave. Vi vender tilbage til dig hurtigst muligt for at drøfte dine ønsker og behov.";

  return (
    <div className="min-h-screen bg-brand-light overflow-x-hidden">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/rulerbg.png" />
      </Helmet>

      {/* Hero Sektion */}
      <section className="relative bg-brand-dark text-white pt-32 sm:pt-36 md:pt-40 min-[1150px]:pt-44 pb-16 md:pb-24 overflow-hidden">
        {/* Baggrundsbillede med stilrent overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="SKT Byg Håndværk"
            className="w-full h-full object-cover object-center scale-105"
          />
          {/* Mørkt overlay med lavere opacitet for større synlighed af baggrundsbilledet */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-brand-dark/55 to-brand-dark/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-brand-dark/30"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 pt-4 sm:pt-6 md:pt-8">
          <div className="max-w-3xl text-left">
            {/* Hovedoverskrift */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-6 uppercase text-white animate-fade-in-up [animation-delay:100ms] fill-mode-forwards opacity-0">
              {heroTitle}
            </h1>

            {/* Brødtekst */}
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl animate-fade-in-up [animation-delay:200ms] fill-mode-forwards opacity-0 font-normal">
              {heroText}
            </p>

            {/* Handlingsknapper / CTA */}
            <div className="flex flex-wrap items-center gap-4 animate-fade-in-up [animation-delay:400ms] fill-mode-forwards opacity-0">
              <Link
                to="/referencegalleri"
                className="inline-flex items-center justify-center gap-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold px-6 py-3.5 transition-all duration-300 hover:scale-[1.02] backdrop-blur-sm uppercase tracking-wider text-xs sm:text-sm"
              >
                <span>{heroSecBtnText}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hurtig Kontaktbar */}
      <section className="relative z-20 bg-brand-sand border-b border-brand-sand-dark py-5 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-brand-accent text-white flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-hard-hat text-base"></i>
              </div>
              <div>
                <p className="text-xs uppercase font-bold text-brand-dark/60 tracking-wider">{quickContactTitle}</p>
                <p className="text-sm sm:text-base font-extrabold text-brand-dark">{quickContactSubtitle}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 sm:gap-8 pt-2 sm:pt-0">
              {/* Telefon */}
              <a
                href="tel:20910019"
                className="flex items-center gap-3 text-brand-dark hover:text-brand-accent transition-colors duration-200 group"
              >
                <i className="fa-solid fa-phone text-xl text-brand-accent group-hover:scale-110 transition-transform"></i>
                <span className="text-lg sm:text-xl font-extrabold tracking-tight">20 91 00 19</span>
              </a>

              {/* Mail */}
              <a
                href="mailto:steffen@skt-byg.dk"
                className="flex items-center gap-3 text-brand-dark hover:text-brand-accent transition-colors duration-200 group"
              >
                <i className="fa-solid fa-envelope text-xl text-brand-accent group-hover:scale-110 transition-transform"></i>
                <span className="text-sm sm:text-base font-bold tracking-tight">steffen@skt-byg.dk</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Kompetencer / Ydelser */}
      <section className="bg-white text-brand-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" direction="up">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-4 uppercase">
              {servicesTitle}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {servicesSubtitle}
            </p>
          </ScrollReveal>

          {/* 3 primære ydelseskort med billeder fra scrape */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "Nybyggeri",
                desc: "Vi opfører moderne og funktionelle nybyggerier i højeste kvalitet tilpasset dine ønsker.",
                img: "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/1595576254044627-0-700.jpg",
                link: "/referencegalleri/nybyggeri"
              },
              {
                title: "Vinduer & døre",
                desc: "Professionel udskiftning og montering af døre og vinduer, der sikrer bedre isolering.",
                img: "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/1595565540712365-0-700.jpg",
                link: "/referencegalleri/vinduer-doere"
              },
              {
                title: "Terrasser & altaner",
                desc: "Skræddersyede træterrasser og altaner, der forlænger uderummet og øger boligens værdi.",
                img: "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/1595575534044699-0-700.jpg",
                link: "/referencegalleri/terrasser-altaner"
              }
            ].map((card, idx) => (
              <ScrollReveal key={idx} delay={idx * 150} direction="up" className="h-full">
                <div className="flex flex-col bg-white border border-gray-100 overflow-hidden shadow-lg h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={card.img}
                      alt={card.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-brand-dark mb-2 uppercase tracking-tight">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 flex-grow">
                      {card.desc}
                    </p>
                    <div>
                      <Link
                        to={card.link}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-dark hover:text-brand-accent transition-colors duration-300 group"
                      >
                        Læs mere <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform duration-300"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Detaljeret liste over hvad vi tilbyder */}
          <ScrollReveal direction="up" delay={100}>
            <div className="relative overflow-hidden bg-brand-sand/50 border border-brand-sand p-8 md:p-12 lg:pr-24">
              {/* Skrå orange sektion i højre side - matcher headerens skrå form */}
              <div 
                className="hidden lg:block absolute top-0 right-0 bottom-0 w-[300px] bg-brand-accent"
                style={{ clipPath: 'polygon(50px 0, 100% 0, 100% 100%, 0 100%)' }}
              ></div>

              <div className="relative z-10 max-w-3xl lg:max-w-[65%]">
                <h3 className="text-2xl md:text-3xl font-extrabold text-brand-dark mb-6 uppercase">
                  {qualityTitle}
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {qualityText}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Nybygninger & tilbygninger",
                    "Døre, vinduer & glarmesterarbejde",
                    "Udbedring af forsikringsskader",
                    "VELUX certificeret montering",
                    "Certificeret Junckers gulvlægger",
                    "Tage & kviste",
                    "Murerafdeling & flisearbejde",
                    "Akustikberegninger & snedkerløsninger",
                    "Isolering & energivejledning",
                    "Online timeregistrering (EG Webtid)"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2.5 text-sm font-semibold text-brand-dark hover:translate-x-1 transition-transform duration-200">
                      <i className="fa-solid fa-check text-brand-dark text-base"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Kundeanmeldelser */}
      <section className="py-20 bg-white border-y border-brand-sand">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" direction="up">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark uppercase">
              {reviewsTitle}
            </h2>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={150}>
            <div className="relative mb-12 px-2 md:px-12">
              {/* Arrows */}
              {reviews.length > visibleCount && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-brand-sand shadow-md flex items-center justify-center text-brand-dark hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all duration-300 cursor-pointer"
                    aria-label="Forrige anmeldelse"
                  >
                    <i className="fa-solid fa-chevron-left text-xs md:text-sm"></i>
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white border border-brand-sand shadow-md flex items-center justify-center text-brand-dark hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all duration-300 cursor-pointer"
                    aria-label="Næste anmeldelse"
                  >
                    <i className="fa-solid fa-chevron-right text-xs md:text-sm"></i>
                  </button>
                </>
              )}

              {/* Carousel Container */}
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{
                    transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
                  }}
                >
                  {reviews.map((review) => {
                    const isExpanded = !!expandedReviews[review.id];
                    const rawText = review.content ? review.content.replace(/<[^>]*>/g, '') : '';
                    const isLong = rawText.length > 150;
                    const displayContent = isLong && !isExpanded 
                      ? rawText.slice(0, 150) + '...' 
                      : review.content;

                    return (
                      <div
                        key={review.id}
                        className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4 flex flex-col"
                      >
                        <div className="bg-white p-8 border border-brand-sand shadow-sm flex flex-col justify-between h-full min-h-[280px] hover:shadow-md transition-shadow duration-300">
                          <div>
                            <div className="flex items-center gap-1 text-brand-accent mb-4">
                              {[...Array(5)].map((_, i) => (
                                <i key={i} className="fa-solid fa-star text-xs"></i>
                              ))}
                            </div>
                            {review.title && (
                              <h4 className="font-bold text-brand-dark mb-3">
                                {review.title}
                              </h4>
                            )}
                            <div className="text-sm text-brand-dark/80 leading-relaxed mb-4">
                              {isLong && !isExpanded ? (
                                <p>{displayContent}</p>
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: displayContent }} />
                              )}
                            </div>
                            {isLong && (
                              <button
                                onClick={() => setExpandedReviews(prev => ({ ...prev, [review.id]: !prev[review.id] }))}
                                className="text-xs font-bold text-brand-dark hover:text-brand-accent transition-colors duration-300 mb-6 flex items-center gap-1 cursor-pointer"
                              >
                                {isExpanded ? 'Vis mindre' : 'Læs mere'}
                                <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
                              </button>
                            )}
                          </div>
                          <div className="flex items-center justify-between pt-4 border-t border-brand-sand text-xs font-bold text-brand-dark/60 mt-auto">
                            <span>{review.author}</span>
                            <span>{review.date}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dots navigation */}
              {reviews.length > visibleCount && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                        currentIndex === idx ? 'bg-brand-accent w-6' : 'bg-brand-sand hover:bg-brand-dark/40'
                      }`}
                      aria-label={`Gå til dias ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollReveal>

          <ScrollReveal className="text-center" direction="up" delay={200}>
            <Link
              to="/anmeldelser"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-dark hover:bg-brand-accent text-white font-bold rounded-none transition-all duration-300 cursor-pointer hover:scale-105 shadow-md"
            >
              {reviewsBtnText} <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Kontaktformular Sektion */}
      <section className="relative overflow-hidden bg-white py-20 border-t border-brand-sand">
        {/* Orange skrå del i højre side */}
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-brand-accent pointer-events-none hidden lg:block [clip-path:polygon(100%_0,100%_100%,0_100%,45%_0)] opacity-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-brand-accent pointer-events-none hidden lg:block [clip-path:polygon(100%_0,100%_100%,20%_100%,60%_0)] opacity-5"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <ScrollReveal direction="left" className="lg:col-span-5 space-y-6">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark tracking-tight uppercase">
              {contactTitle}
            </h2>
            <p className="text-brand-dark/70 leading-relaxed">
              {contactText}
            </p>
            <div className="divide-y divide-brand-sand border-y border-brand-sand py-2 my-6">
              <div className="flex items-center gap-4 py-3 group">
                <div className="flex h-10 w-10 items-center justify-center text-brand-accent text-lg group-hover:scale-110 transition-transform duration-300">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <span className="font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300">20 91 00 19</span>
              </div>
              <div className="flex items-center gap-4 py-3 group">
                <div className="flex h-10 w-10 items-center justify-center text-brand-accent text-lg group-hover:scale-110 transition-transform duration-300">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <span className="font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300">steffen@skt-byg.dk</span>
              </div>
              <div className="flex items-center gap-4 py-3 group">
                <div className="flex h-10 w-10 items-center justify-center text-brand-accent text-lg group-hover:scale-110 transition-transform duration-300">
                  <i className="fa-solid fa-map-marker-alt"></i>
                </div>
                <span className="font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300">Filippavænget 12, 8464 Galten</span>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal direction="right" className="lg:col-span-7 w-full">
            <div className="p-0">
              <ContactForm subject="Henvendelse fra Forsiden" />
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
